export const TransactionType = {
  TICKET_PURCHASE: 'TICKET_PURCHASE',
  BOOKING_ESCROW: 'BOOKING_ESCROW',
  ESCROW_RELEASE: 'ESCROW_RELEASE',
  PLATFORM_FEE: 'PLATFORM_FEE',
  VENDOR_PAYOUT: 'VENDOR_PAYOUT',
  HOST_PAYOUT: 'HOST_PAYOUT',
  REFUND: 'REFUND',
} as const;
export type TransactionType = (typeof TransactionType)[keyof typeof TransactionType];

export const TransactionStatus = {
  PENDING: 'PENDING',
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
  REVERSED: 'REVERSED',
} as const;
export type TransactionStatus = (typeof TransactionStatus)[keyof typeof TransactionStatus];

export const PayoutStatus = {
  PENDING: 'PENDING',
  PROCESSING: 'PROCESSING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
} as const;
export type PayoutStatus = (typeof PayoutStatus)[keyof typeof PayoutStatus];

export const PaymentStatus = {
  REQUIRES_PAYMENT: 'REQUIRES_PAYMENT',
  PROCESSING: 'PROCESSING',
  SUCCEEDED: 'SUCCEEDED',
  FAILED: 'FAILED',
  CANCELLED: 'CANCELLED',
  REFUNDED: 'REFUNDED',
} as const;
export type PaymentStatus = (typeof PaymentStatus)[keyof typeof PaymentStatus];

export const ReplayPurchaseStatus = {
  PENDING_PAYMENT: 'PENDING_PAYMENT',
  ACTIVE: 'ACTIVE',
  EXPIRED: 'EXPIRED',
  REFUNDED: 'REFUNDED',
} as const;
export type ReplayPurchaseStatus = (typeof ReplayPurchaseStatus)[keyof typeof ReplayPurchaseStatus];

export const AccessReason = {
  REGISTERED: 'REGISTERED',
  PUBLIC: 'PUBLIC',
  EMAIL_MATCH: 'EMAIL_MATCH',
  DOMAIN_MATCH: 'DOMAIN_MATCH',
  USER_ID_MATCH: 'USER_ID_MATCH',
  PHONE_MATCH: 'PHONE_MATCH',
  NOT_ON_ALLOWLIST: 'NOT_ON_ALLOWLIST',
} as const;
export type AccessReason = (typeof AccessReason)[keyof typeof AccessReason];

export const EntitlementStatus = {
  NONE: 'NONE',
  PENDING_PAYMENT: 'PENDING_PAYMENT',
  ACTIVE: 'ACTIVE',
  REFUNDED: 'REFUNDED',
  CANCELLED: 'CANCELLED',
} as const;
export type EntitlementStatus = (typeof EntitlementStatus)[keyof typeof EntitlementStatus];

export const PlatformChargeStatus = {
  PENDING: 'PENDING',
  SETTLED: 'SETTLED',
  WAIVED: 'WAIVED',
  VOIDED: 'VOIDED',
  REFUNDED: 'REFUNDED',
} as const;
export type PlatformChargeStatus = (typeof PlatformChargeStatus)[keyof typeof PlatformChargeStatus];

export const CtaMode = {
  BUY_TICKET: 'BUY_TICKET',
  COMPLETE_PAYMENT: 'COMPLETE_PAYMENT',
  WATCH_NOW: 'WATCH_NOW',
  WATCH_REPLAY: 'WATCH_REPLAY',
  REGISTER_FREE: 'REGISTER_FREE',
  WAITLIST: 'WAITLIST',
  UNAVAILABLE: 'UNAVAILABLE',
  REPLAY_PROCESSING: 'REPLAY_PROCESSING',
  /** Invite-only private event and the viewer is not on the guest list. */
  NOT_INVITED: 'NOT_INVITED',
} as const;
export type CtaMode = (typeof CtaMode)[keyof typeof CtaMode];

export const PayoutState = {
  REQUESTED: 'REQUESTED',
  AUTHORIZED: 'AUTHORIZED',
  SENT: 'SENT',
  SETTLED: 'SETTLED',
  FAILED: 'FAILED',
  REVERSED: 'REVERSED',
  CLASSIFICATION_REJECTED: 'CLASSIFICATION_REJECTED',
  UNRESOLVED: 'UNRESOLVED',
} as const;
export type PayoutState = (typeof PayoutState)[keyof typeof PayoutState];

/**
 * Lifecycle of a gateway contract funding intent (§8.2 route 1).
 * Mirrors the backend `ContractFundingIntentStatus` enum exactly.
 * `FUNDED`, `CANCELLED` and `QUARANTINED` are terminal.
 */
export const ContractFundingIntentStatus = {
  AWAITING_PAYMENT: 'AWAITING_PAYMENT',
  PROCESSING: 'PROCESSING',
  FUNDED: 'FUNDED',
  EXPIRED: 'EXPIRED',
  CANCELLED: 'CANCELLED',
  QUARANTINED: 'QUARANTINED',
} as const;
export type ContractFundingIntentStatus =
  (typeof ContractFundingIntentStatus)[keyof typeof ContractFundingIntentStatus];

/**
 * Which route funds a contract escrow. The client names it — a silent
 * fallback to a card charge when a balance debit was expected is not a
 * behaviour the payer can audit.
 */
export const ContractFundingSource = {
  /** Debits the Host's available earnings. Settles immediately. */
  BALANCE: 'BALANCE',
  /** Opens a gateway checkout. The escrow stays unfunded until it is paid. */
  GATEWAY: 'GATEWAY',
} as const;
export type ContractFundingSource =
  (typeof ContractFundingSource)[keyof typeof ContractFundingSource];

/** Mirrors the backend `BookingEscrowState` enum exactly. */
export const BookingEscrowState = {
  AWAITING_FUNDING: 'AWAITING_FUNDING',
  FUNDED: 'FUNDED',
  RELEASED: 'RELEASED',
  REFUNDED: 'REFUNDED',
  DISPUTED: 'DISPUTED',
  CANCELLED: 'CANCELLED',
  FAILED: 'FAILED',
} as const;
export type BookingEscrowState = (typeof BookingEscrowState)[keyof typeof BookingEscrowState];

/**
 * Which profile a payouts call acts for. The Wallet itself is account-level
 * (§2.1: one Wallet per account), but the settlement account money leaves to
 * belongs to a profile, so the caller names which one.
 */
export const SettlementProfileType = {
  HOST: 'HOST',
  VENDOR: 'VENDOR',
} as const;
export type SettlementProfileType =
  (typeof SettlementProfileType)[keyof typeof SettlementProfileType];
